import { useEffect } from "react";

import orderBy from "lodash/orderBy";
import { useParams } from "react-router";

import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { ProgramDetailsLoader } from "@/components/pages_logged_in/program/components/ProgramDetailsLoader";
import { ProgramTimeline } from "@/components/pages_logged_in/program/components/program-timeline/ProgramTimeline";
import { isCloseEarlyComponent } from "@/components/pages_logged_in/program/components/utils";
import { PermissionErrorPage } from "@/components/pages_public/403";
import {
  ComponentTypeEnum,
  useProgramComponentsQueryLazyQuery,
} from "@/graphql";
import { LeadComponent, ParticipantComponent } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

import { ProgramComponentTable } from "./ProgramComponentTable";

const isOneOneCoachingChildren = (type: ComponentTypeEnum) =>
  [
    ComponentTypeEnum.OneOneCoachingIntro,
    ComponentTypeEnum.OneOneCoachingSession,
  ].includes(type);

export function ProgramDetails() {
  const { id } = useParams();

  const [fetchComponents, { data: componentsData, loading: loadingComponent }] =
    useProgramComponentsQueryLazyQuery();

  useEffect(() => {
    if (id) {
      fetchComponents({ variables: { programId: id } });
    }
  }, [id]);

  if (loadingComponent && !componentsData) {
    return (
      <ProgramDetailsLoader
        includeSidebar={true}
        hasHeader={false}
        sizeNavWidth={0}
      />
    );
  }

  const leadsComponents = ensureNonNullArray(
    componentsData?.programComponents?.leadsComponents,
  );

  const participantsComponents = ensureNonNullArray(
    componentsData?.programComponents?.participantsComponents,
  );

  const isLead =
    leadsComponents.length > 0 &&
    isProgramLead(leadsComponents[0]?.program?.id);

  const getTargetDueDate = (
    component: LeadComponent | ParticipantComponent,
  ) => {
    const isEndedEarly = isCloseEarlyComponent(component);
    let targetDueDate = component?.targetDueDate || "";

    // if not lead, we replace the targetDueDate with the closedAt if the component is ended early
    // otherwise, we keep the targetDueDate or targetEndDate
    if (!isLead) {
      if (isEndedEarly) {
        targetDueDate = component?.closedAt || "";
      } else {
        targetDueDate =
          component?.targetDueDate || component?.targetEndDate || "";
      }
    }

    return targetDueDate;
  };

  const components = (
    leadsComponents.length > 0 ? leadsComponents : participantsComponents
  )
    .filter((component) => !isOneOneCoachingChildren(component?.type))
    .map((component: LeadComponent | ParticipantComponent) => {
      const targetDueDate = getTargetDueDate(component);

      return {
        ...component,
        targetDueDate,
        childComponents: orderBy(
          component.childComponents
            ?.filter((child) => isOneOneCoachingChildren(child?.type))
            .map((child) => ({
              ...child,
              // @ts-expect-error - only available on LeadComponent
              requiredSendLaunchEmail: component?.requiredSendLaunchEmail,
              visibleUpcomingQuestions: component.visibleUpcomingQuestions,
              // @ts-expect-error - only available on LeadComponent
              forAllParticipants: component?.forAllParticipants,
              parentComponent: component,
              programId: component.programId,
              targetDueDate: getTargetDueDate({
                ...child,
                visibleUpcomingQuestions: component.visibleUpcomingQuestions,
              }),
            })),
          ["targetStartDate", "id"],
          ["asc", "asc"],
        ),
      };
    });

  if (!id || !componentsData) return <PermissionErrorPage />;

  return (
    <Box mb={4}>
      <ProgramTimeline programId={id} components={components} />
      <ProgramComponentTable components={components} isLead={isLead} />
      <Flex width="100%" flexDirection={["column", "column", "column", "row"]}>
        <Nav />
      </Flex>
    </Box>
  );
}
